import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Check, X } from 'lucide-react';
import type { Paginated, TimesheetState } from '@timesheet/shared';
import { apiOrThrow } from '@/lib/api';
import { useAuth } from '@/hooks/useAuth';
import { DataTable, type Column } from '@/components/ui/DataTable';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Card, CardBody } from '@/components/ui/Card';
import { Alert } from '@/components/ui/Alert';
import { Modal } from '@/components/ui/Modal';
import { Select } from '@/components/ui/Select';
import { StateBadge } from '@/components/timesheet/StateBadge';

interface EntryRow {
  id: string;
  ownerId: string;
  ownerUsername: string | null;
  ownerFullName: string | null;
  workDate: string;
  clientCode: string | null;
  clientName: string | null;
  engagementCode: string | null;
  engagementName: string | null;
  activityCode: string | null;
  activityName: string | null;
  hours: number;
  description: string | null;
  state: TimesheetState;
}

function daysAgo(n: number): string {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - n);
  return d.toISOString().slice(0, 10);
}
function todayDate(): string {
  return new Date().toISOString().slice(0, 10);
}

/**
 * Approval queue for Managers + Partners.
 *
 * Default filter:
 *   - Manager → state=submitted  (entries to approve into manager_approved)
 *   - Partner → state=manager_approved (entries to approve into partner_approved)
 *
 * User can switch filter to see other states.
 */
export function TimesheetReportPage() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const isPartner = user?.role === 'Partner' || user?.role === 'Admin';
  const defaultFilterState: TimesheetState = isPartner ? 'manager_approved' : 'submitted';

  const [fromDate, setFromDate] = useState(daysAgo(60));
  const [toDate, setToDate] = useState(todayDate());
  const [stateFilter, setStateFilter] = useState<TimesheetState>(defaultFilterState);
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  const listQuery = useQuery({
    queryKey: ['timesheet', 'report', { fromDate, toDate, stateFilter }],
    queryFn: () => {
      const params = new URLSearchParams({
        fromDate,
        toDate,
        state: stateFilter,
        pageSize: '500',
      });
      return apiOrThrow<Paginated<EntryRow>>(`/timesheet?${params.toString()}`);
    },
  });

  const transitionMut = useMutation({
    mutationFn: (vars: { entryIds: string[]; toState: TimesheetState; comment?: string }) =>
      apiOrThrow<{ transitioned: number }>('/timesheet/transition', {
        method: 'POST',
        json: vars,
      }),
    onSuccess: () => {
      setSelected(new Set());
      setRejectOpen(false);
      setRejectReason('');
      qc.invalidateQueries({ queryKey: ['timesheet'] });
    },
  });

  const items = listQuery.data?.items ?? [];

  // Approval target depends on current state
  const approveTarget: TimesheetState | null =
    stateFilter === 'submitted'
      ? 'manager_approved'
      : stateFilter === 'manager_approved'
        ? 'partner_approved'
        : null;

  const canApprove = approveTarget !== null && selected.size > 0;
  const canReject =
    (stateFilter === 'submitted' || stateFilter === 'manager_approved') && selected.size > 0;

  const totalSelectedHours = useMemo(
    () => items.filter((e) => selected.has(e.id)).reduce((s, e) => s + e.hours, 0),
    [items, selected],
  );

  const toggleSelect = (id: string) => {
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const selectAll = () => setSelected(new Set(items.map((e) => e.id)));
  const clearSelection = () => setSelected(new Set());

  const columns: Column<EntryRow>[] = [
    {
      key: 'select',
      header: '',
      render: (r) => (
        <input
          type="checkbox"
          checked={selected.has(r.id)}
          onChange={() => toggleSelect(r.id)}
          className="h-4 w-4 rounded border-brand-gray-light text-brand-red"
          aria-label={`Select entry ${r.id}`}
        />
      ),
      className: 'w-8',
    },
    {
      key: 'workDate',
      header: 'Date',
      className: 'whitespace-nowrap font-mono text-xs',
    },
    {
      key: 'owner',
      header: 'Staff',
      render: (r) => (
        <div>
          <div className="font-medium">{r.ownerFullName ?? '—'}</div>
          <div className="text-xs text-brand-gray-mid">@{r.ownerUsername ?? '—'}</div>
        </div>
      ),
    },
    {
      key: 'work',
      header: 'Client / Engagement / Activity',
      render: (r) => (
        <div className="min-w-0 max-w-md">
          <div className="truncate font-medium text-brand-charcoal">{r.clientName ?? '—'}</div>
          <div className="truncate text-xs text-brand-gray-mid">
            {r.engagementCode} · {r.activityName}
          </div>
          {r.description && (
            <p className="mt-0.5 truncate text-xs italic text-brand-charcoal/70">
              "{r.description}"
            </p>
          )}
        </div>
      ),
      grow: true,
    },
    {
      key: 'hours',
      header: 'Hours',
      render: (r) => <span className="font-mono font-medium">{r.hours.toFixed(2)}</span>,
      className: 'whitespace-nowrap text-right',
    },
    {
      key: 'state',
      header: 'Status',
      render: (r) => <StateBadge state={r.state} />,
    },
  ];

  return (
    <div className="mx-auto max-w-7xl space-y-4">
      <div>
        <h1 className="text-2xl font-semibold text-brand-charcoal">Timesheet approval</h1>
        <p className="mt-1 text-sm text-brand-gray-mid">
          {isPartner
            ? "Partner approval queue — review entries already approved by Manager."
            : "Manager approval queue — review entries submitted by your team."}
        </p>
      </div>

      <Card>
        <CardBody className="flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-xs font-medium text-brand-gray-mid">From</label>
            <Input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="w-40"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-brand-gray-mid">To</label>
            <Input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className="w-40"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-brand-gray-mid">State</label>
            <Select
              value={stateFilter}
              onChange={(e) => {
                setStateFilter(e.target.value as TimesheetState);
                clearSelection();
              }}
              className="w-44"
            >
              <option value="submitted">Submitted</option>
              <option value="manager_approved">Manager approved</option>
              <option value="partner_approved">Partner approved</option>
              <option value="rejected">Rejected</option>
              <option value="draft">Draft</option>
            </Select>
          </div>

          <div className="ml-auto flex items-center gap-2">
            {selected.size > 0 && (
              <span className="text-sm text-brand-gray-mid">
                {selected.size} selected · {totalSelectedHours.toFixed(2)}h
              </span>
            )}
            {selected.size === 0 && items.length > 0 && (
              <Button variant="ghost" size="sm" onClick={selectAll}>
                Select all
              </Button>
            )}
            {canReject && (
              <Button variant="danger" size="md" onClick={() => setRejectOpen(true)}>
                <X className="h-4 w-4" aria-hidden />
                <span>Reject</span>
              </Button>
            )}
            {canApprove && (
              <Button
                variant="primary"
                size="md"
                loading={transitionMut.isPending}
                onClick={() => {
                  if (
                    confirm(
                      `Approve ${selected.size} entr${selected.size === 1 ? 'y' : 'ies'} (${totalSelectedHours.toFixed(2)}h)?`,
                    )
                  ) {
                    transitionMut.mutate({
                      entryIds: [...selected],
                      toState: approveTarget!,
                    });
                  }
                }}
              >
                <Check className="h-4 w-4" aria-hidden />
                <span>Approve</span>
              </Button>
            )}
          </div>
        </CardBody>
      </Card>

      {transitionMut.isError && (
        <Alert tone="danger">{(transitionMut.error as Error).message}</Alert>
      )}

      <DataTable
        columns={columns}
        rows={items}
        loading={listQuery.isPending}
        total={listQuery.data?.total}
        rowKey={(r) => r.id}
        emptyMessage={`No entries in '${stateFilter}' state for this date range.`}
      />

      {/* Reject reason modal */}
      <Modal
        open={rejectOpen}
        onClose={() => setRejectOpen(false)}
        title={`Reject ${selected.size} entr${selected.size === 1 ? 'y' : 'ies'}`}
        description="The submitter will see this reason and can recall to draft."
        footer={
          <>
            <Button variant="secondary" onClick={() => setRejectOpen(false)} type="button">
              Cancel
            </Button>
            <Button
              variant="danger"
              loading={transitionMut.isPending}
              type="button"
              onClick={() =>
                transitionMut.mutate({
                  entryIds: [...selected],
                  toState: 'rejected',
                  comment: rejectReason.trim() || undefined,
                })
              }
            >
              Reject
            </Button>
          </>
        }
      >
        <div>
          <label
            htmlFor="reject-reason"
            className="block text-sm font-medium text-brand-charcoal"
          >
            Reason
          </label>
          <textarea
            id="reject-reason"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            rows={3}
            maxLength={500}
            placeholder="e.g. activity code doesn't match the engagement scope"
            className="mt-1 w-full rounded border border-brand-gray-light bg-white px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-blue"
          />
          <p className="mt-1 text-xs text-brand-gray-mid">Max 500 characters</p>
        </div>
      </Modal>
    </div>
  );
}
